import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const getCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches && e.touches.length > 0) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const c = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(c.x, c.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const c = getCoords(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    ctx.lineTo(c.x, c.y);
    ctx.stroke();
  };

  const end = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    drawing.current = false;
    onChange(canvasRef.current.toDataURL('image/png'));
  };

  const clear = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    onChange('');
  };

  return (
    <div className="space-y-2">
      <canvas
        ref={canvasRef}
        width={400}
        height={150}
        data-testid="document-signature-canvas"
        className="border-2 border-slate-400 rounded cursor-crosshair bg-white w-full"
        style={{ touchAction: 'none', maxWidth: '100%' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <Button type="button" variant="outline" size="sm" onClick={clear} data-testid="clear-document-signature-btn">
        Clear Signature
      </Button>
    </div>
  );
};

export const DocumentsToSign = ({ token, apiCall }) => {
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signature, setSignature] = useState('');
  const [privacyAck, setPrivacyAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statuses, tpls] = await Promise.all([
        apiCall('/my/signed-documents', 'GET', null, token),
        apiCall('/documents/templates', 'GET', null, token)
      ]);
      setDocs(statuses || []);
      setTemplates(tpls || []);
    } catch {
      toast.error('Failed to load documents');
    }
  }, [apiCall, token]);

  useEffect(() => { load(); }, [load]);

  const handleSign = async () => {
    if (!signature) {
      toast.error('Please draw your signature first');
      return;
    }
    if (!privacyAck) {
      toast.error('Please confirm you have read the Privacy Notice');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiCall('/documents/sign', 'POST', { document_type: signingDoc.document_type, signature, privacy_acknowledged: true }, token);
      toast.success(res.message || 'Document signed');
      setSigningDoc(null);
      setSignature('');
      setPrivacyAck(false);
      load();
    } catch (e) {
      toast.error(e.detail || 'Failed to sign document');
    }
    setSubmitting(false);
  };

  const template = signingDoc ? templates.find(t => t.document_type === signingDoc.document_type) : null;
  const pendingCount = docs.filter(d => !d.signed).length;

  return (
    <div className="space-y-4" data-testid="documents-to-sign-section">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            Documents to Sign
            {pendingCount > 0 ? (
              <Badge className="bg-orange-500" data-testid="pending-docs-badge">{pendingCount} pending</Badge>
            ) : (
              <Badge className="bg-green-600" data-testid="all-docs-signed-badge">All signed ✓</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {docs.map((doc) => (
            <div
              key={doc.document_type}
              className={`flex items-center justify-between p-4 rounded-lg border ${doc.signed ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}
              data-testid={`doc-row-${doc.document_type}`}
            >
              <div>
                <p className="font-medium">{doc.name}</p>
                {doc.signed ? (
                  <p className="text-xs text-green-700">
                    ✓ Signed on {new Date(doc.signed_at).toLocaleDateString('en-GB')} at {new Date(doc.signed_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                ) : (
                  <p className="text-xs text-orange-700">⏳ Awaiting your signature</p>
                )}
              </div>
              {doc.signed ? (
                <a
                  href={`${BACKEND_URL}${doc.pdf_url}?token=${token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`view-signed-pdf-${doc.document_type}`}
                >
                  <Button variant="outline" size="sm">📄 View PDF</Button>
                </a>
              ) : (
                <Button
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid={`sign-doc-btn-${doc.document_type}`}
                  onClick={() => { setSigningDoc(doc); setSignature(''); }}
                >
                  Read & Sign
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!signingDoc} onOpenChange={(open) => { if (!open) { setSigningDoc(null); setSignature(''); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{signingDoc?.name}</DialogTitle>
            <DialogDescription>Read the document below, then draw your signature to agree.</DialogDescription>
          </DialogHeader>
          {template && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-slate-50 max-h-72 overflow-y-auto text-sm whitespace-pre-wrap" data-testid="document-content-text">
                {template.content}
              </div>
              <div>
                <p className="font-semibold text-sm mb-2">Draw your signature below to agree:</p>
                <SignCanvas onChange={setSignature} />
              </div>
              <p className="text-xs text-slate-500">
                By signing you agree to this document. Your signature will be recorded with a secure UTC timestamp and your IP address, and a signed PDF copy will be generated.
              </p>
              <div className="flex items-start gap-2 bg-slate-50 border rounded p-2">
                <input
                  type="checkbox"
                  id="sign-privacy-ack"
                  data-testid="sign-privacy-ack-checkbox"
                  checked={privacyAck}
                  onChange={(e) => setPrivacyAck(e.target.checked)}
                  className="mt-0.5"
                />
                <label htmlFor="sign-privacy-ack" className="text-xs text-slate-600">
                  I have read the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline" data-testid="sign-privacy-link">
                    Privacy Notice
                  </a>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setSigningDoc(null); setSignature(''); setPrivacyAck(false); }}>Cancel</Button>
                <Button
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={!signature || !privacyAck || submitting}
                  onClick={handleSign}
                  data-testid="submit-document-signature-btn"
                >
                  {submitting ? 'Signing...' : 'Sign & Submit'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
